<template>
  <div class="chart-bar-vertical" />
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themesAnimated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themesAnimated);

export default {
  name: 'BarChartX',
  props: {
    type: {
      type: String,
      required: true,
    },
  },
  watch: {
    type(newVal) {
      this.filterChart(newVal);
    },
  },
  mounted() {
    // load data
    const dataSource = new am4core.DataSource();
    dataSource.url = `${process.env.BASE_URL}data/bar-x.csv`;
    dataSource.parser = new am4core.CSVParser();
    dataSource.parser.options.emptyAs = '';
    dataSource.parser.options.useColumnNames = true;

    dataSource.load();

    dataSource.events.on('done', ({ data }) => {
      // sort type prop by alphabet and Overall business growth to top
      this.sanitizedData = data
        .sort((a, b) => (a.type === b.type ? 0 : +(a.type > b.type) || -1))
        .sort((a, b) => (b.type === 'Overall business growth') - (a.type === 'Overall business growth'));
      // set default "type"
      this.typeDefault = this.sanitizedData[0].type;

      // pass data to parent
      this.$emit('data-sanitized', this.sanitizedData);
      this.$emit('update:type', this.typeDefault);

      this.chart = this.createChart();

      // create axes
      this.categoryAxis = this.createAxisX();
      this.valueAxis = this.createAxisY();

      // create series
      this.series = this.createSeries();
    });
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    createChart() {
      // create XY chart
      const chart = am4core.create(this.$el, am4charts.XYChart);

      // set initial chart data
      chart.data = this.sanitizedData
        .filter((d) => d.type === this.typeDefault)
        .map(({ type, ...keepProps }) => {
          // new object - sanitizedData objects won't be mutated
          return { ...keepProps };
        });

      // settings
      chart.padding(5, 0, 0, 0);
      chart.zoomOutButton.disabled = true;

      return chart;
    },
    createAxisX() {
      // x-axis - category axis
      const categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      // logic settings
      categoryAxis.dataFields.category = 'impact';

      // style settings
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.labels.template.rotation = 0;
      categoryAxis.renderer.labels.template.fill = '#121212';
      categoryAxis.renderer.labels.template.fontSize = 14;
      categoryAxis.renderer.labels.template.textAlign = 'middle';
      categoryAxis.renderer.labels.template.horizontalCenter = 'middle';
      categoryAxis.renderer.labels.template.verticalCenter = 'top';
      categoryAxis.renderer.labels.template.maxWidth = 100;
      categoryAxis.renderer.labels.template.wrap = true;

      // mobile setting
      if (this.$breakpoint.mobile) {
        categoryAxis.renderer.labels.template.fontSize = 10.5;

        categoryAxis.renderer.labels.template.dx = 22.5;
        categoryAxis.renderer.labels.template.dy = -5;
        categoryAxis.renderer.labels.template.rotation = -55;
        categoryAxis.renderer.labels.template.textAlign = 'end';
        categoryAxis.renderer.labels.template.horizontalCenter = 'right';
        categoryAxis.renderer.labels.template.verticalCenter = 'bottom';
      }

      categoryAxis.events.once('sizechanged', (ev) => {
        const axis = ev.target;

        // calculate per cell width
        const cellWidth = axis.pixelWidth / (axis.endIndex - axis.startIndex);

        // set widths of grid cell and label cell
        axis.renderer.minGridDistance = cellWidth - 10;
        // axis.renderer.labels.template.maxWidth = cellWidth;

        // if (this.$breakpoint.mobile) {
        //   // axis.renderer.labels.template.fontSize = 12;

        //   axis.renderer.labels.template.rotation = -45;
        //   axis.renderer.labels.template.textAlign = 'end';
        //   axis.renderer.labels.template.horizontalCenter = 'right';
        //   axis.renderer.labels.template.verticalCenter = 'middle';
        // } else if (this.$breakpoint.tablet) {
        //   // axis.renderer.labels.template.fontSize = 14;
        //   axis.renderer.labels.template.rotation = 0;
        //   axis.renderer.labels.template.textAlign = 'middle';
        //   axis.renderer.labels.template.horizontalCenter = 'middle';
        //   axis.renderer.labels.template.verticalCenter = 'top';
        // } else {
        //   // axis.renderer.labels.template.fontSize = 16;

        //   axis.renderer.labels.template.rotation = 0;
        //   axis.renderer.labels.template.textAlign = 'middle';
        //   axis.renderer.labels.template.horizontalCenter = 'middle';
        //   axis.renderer.labels.template.verticalCenter = 'top';
        // }

        // if (cellWidth - axis.renderer.labels.template.measuredWidth <= 50) {
        //   console.log('rotate width');

        //   axis.renderer.labels.template.rotation = -90;
        //   axis.renderer.labels.template.horizontalCenter = 'right';
        //   axis.renderer.labels.template.verticalCenter = 'middle';
        // } else {
        //   console.log('stay width');

        //   axis.renderer.labels.template.rotation = 0;
        // }
      });

      return categoryAxis;
    },
    createAxisY() {
      // y-axis - value axis
      const valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
      // logic settings
      valueAxis.min = 0;
      valueAxis.extraMax = 0.1;
      // valueAxis.max = 50;
      // valueAxis.strictMinMax = true;
      valueAxis.renderer.labels.template.adapter.add('text', (label) => {
        return `${label}%`;
      });
      // style settings
      valueAxis.renderer.grid.template.stroke = '#F0F0F0';
      valueAxis.renderer.grid.template.strokeOpacity = 1;
      valueAxis.renderer.labels.template.fontSize = 12;
      valueAxis.renderer.labels.template.fill = '#595959';

      // valueAxis.events.on('selectionextremeschanged', ({ target }) => {
      //   console.log(target);
      //   // const currentZoom = target.maxZoom;

      //   // target.maxZoom = currentZoom + 5;

      //   // console.log(target.maxZoomed);

      //   // target.zoomToValues(0, target.maxZoomed + 0.5);
      // })

      return valueAxis;
    },
    createSeries() {
      const series = this.chart.series.push(new am4charts.ColumnSeries());

      // series settings
      series.dataFields.categoryX = 'impact';
      series.dataFields.valueY = 'percentage';
      series.columns.template.propertyFields.fill = 'color';
      series.columns.template.propertyFields.stroke = 'color';

      // create label bullets
      const bullet = series.bullets.push(new am4charts.LabelBullet());
      bullet.label.text = '{percentage}%';
      // bullet.label.dy = -10;
      bullet.label.verticalCenter = 'bottom';
      bullet.label.fontSize = 14;
      bullet.label.fontWeight = 500;
      bullet.label.fill = '#121212';
      bullet.label.truncate = false;
      bullet.label.hideOversized = false;

      return series;
    },
    filterChart(type) {
      // get data by selected type
      const selectedSet = this.sanitizedData.filter((d) => d.type === type);

      // mutate existing value, so chart will animate the difference
      this.chart.data.forEach((d) => {
        d.percentage = selectedSet.length > 0 ? selectedSet.find((i) => i.impact === d.impact).percentage : 0;
      });
      this.chart.invalidateRawData();
    },
  },
};
</script>

<style lang="scss" scoped>
.chart-bar-vertical {
  width: 100%;
  height: 400px;
}
</style>
